import React, { useEffect, useMemo, useState } from "react";
import {
  ActionMenu,
  Breaker,
  Button,
  Flexbox,
  InputField,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import { Text } from "../../Theme/Typography";
import { CloseCircle } from "../../Theme/Icons";
import { globalTheme } from "../../Theme/Theme";
import { PopupBody, PopupWrapper } from "./CommonPopup";
import {
  clearCoworkSliceStates,
  coworkData,
  getEquipmentList,
  postEquipment,
} from "../../../redux/cowork/coworkSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const ADD_NEW = "__add_new__";

// Restores the old "Add Equipment" flow (AllRooms → AddEquipmentPopup) in the
// new UI. Creates a new equipment type (name + category) via postEquipment and
// refreshes the equipment list so it shows up in the room form's picker.
const AddEquipmentPopup = ({ isActive, togglePopup }) => {
  const [equipmentName, setEquipmentName] = useState("");
  const [category, setCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");

  const dispatch = useDispatch();
  const {
    isCoworkSliceFetching,
    isCoworkSliceSuccess,
    isCoworkSliceError,
    coworkSliceErrorMessage,
    equipmentList,
  } = useSelector(coworkData);

  useEffect(() => {
    if (!isActive) return;
    setEquipmentName("");
    setCategory("");
    setNewCategory("");
    dispatch(getEquipmentList());
  }, [isActive]);

  useEffect(() => {
    if (isActive && isCoworkSliceSuccess) {
      dispatch(getEquipmentList());
      dispatch(clearCoworkSliceStates());
      togglePopup(false);
    }
  }, [isCoworkSliceSuccess]);

  useEffect(() => {
    if (isCoworkSliceError) {
      toast.error(coworkSliceErrorMessage, {
        autoClose: 3000,
      });
      dispatch(clearCoworkSliceStates());
    }
  }, [isCoworkSliceError, coworkSliceErrorMessage]);

  const categoryOptions = useMemo(() => {
    const cats = [
      ...new Set(
        (equipmentList || [])
          .map((e) => e.category)
          .filter((c) => c && c.trim() !== "")
      ),
    ].map((c) => ({ dropmenu: c, value: c }));
    return [...cats, { dropmenu: "Add New Category", value: ADD_NEW }];
  }, [equipmentList]);

  const selectedCategoryLabel =
    category === ADD_NEW
      ? "Add New Category"
      : category || "Select Category";

  const addEquipment = () => {
    if (!equipmentName?.trim()) {
      toast.error("Please enter equipment name", {
        autoClose: 3000,
      });
      return;
    }
    if (!category) {
      toast.error("Please select a category", {
        autoClose: 3000,
      });
      return;
    }
    if (category === ADD_NEW && !newCategory?.trim()) {
      toast.error("Please enter a new category name", {
        autoClose: 3000,
      });
      return;
    }
    dispatch(
      postEquipment({
        equipmentName: equipmentName.trim(),
        category: category === ADD_NEW ? newCategory.trim() : category,
      })
    );
  };

  return (
    <PopupWrapper className={`${isActive ? "active" : ""}`}>
      <PopupBody maxWidth={480} padding={24}>
        <Flexbox spaceBW alignCenter>
          <Text font18 inter semiBold white maxWidth="max-content">
            Add New Equipment
          </Text>
          <CloseCircle className={"pointer"} onClick={() => togglePopup(false)} />
        </Flexbox>
        <Breaker marginTop={16} marginBottom={20} borderColor={globalTheme.colors.white1} />

        <Flexbox column gap={16} alignStart>
          <InputField
            label="Equipment Name"
            isImp
            placeholder="Enter Equipment Name"
            onChange={(e) => setEquipmentName(e.target.value)}
            value={equipmentName}
          />

          <Flexbox column gap={8} alignStart maxWidth="max-content">
            <Text font14 inter medium white>
              Category <span style={{ color: globalTheme.colors.red }}>*</span>
            </Text>
            <WrapperBox
              background={globalTheme.colors.nuetral2}
              maxWidth="max-content"
              padding={"12px"}
              borderRadius={50}
              borderColor={globalTheme.colors.nuetral2}
              maxHeight={"max-content"}
              className={"pointer"}
              overflow={"visible"}
            >
              <ActionMenu
                btnName={selectedCategoryLabel}
                selectedValue={category}
                menuList={categoryOptions}
                onSelect={(value) => {
                  setCategory(value);
                  if (value !== ADD_NEW) setNewCategory("");
                }}
              />
            </WrapperBox>
          </Flexbox>

          {category === ADD_NEW && (
            <InputField
              label="New Category"
              isImp
              placeholder="Enter New Category Name"
              onChange={(e) => setNewCategory(e.target.value)}
              value={newCategory}
            />
          )}
        </Flexbox>

        <Flexbox alignCenter gap={12} justiCenter className="mt20">
          <Button
            whiteFilled
            buttonMd
            name={"Cancel"}
            disabled={isCoworkSliceFetching}
            onClick={() => togglePopup(false)}
          />
          <Button
            primaryFilled
            buttonMd
            name={"Add Equipment"}
            onClick={addEquipment}
            disabled={isCoworkSliceFetching}
            isLoading={isCoworkSliceFetching}
          />
        </Flexbox>
      </PopupBody>
    </PopupWrapper>
  );
};

export default AddEquipmentPopup;
